import { STAGE_TITLES, type ArmSummary, type RecognitionArm } from '@/types/domain'

/**
 * The arm picker's options.
 *
 * The point of the picker is to show the same scans as a *different* reading
 * read them, so the one thing it must never be vague about is which reading
 * is on screen and which one is actually delivered. `DELIVERED` is that
 * reading; every other option is a stored variant and is labelled as one.
 */

/** The picker's value for "the values this template delivers". Not an arm:
 *  the delivered result is the document's own, whichever arm produced it. */
export const DELIVERED = ''

/** Which stored reading a request means: an arm, and the model that produced
 *  it where there is one (`ocr` and `human` run no model). */
export interface ArmRequest {
  arm: string
  model?: string
}

export interface ArmOption extends ArmRequest {
  /** `DELIVERED`, or the option's key — see `armValue`. */
  value: string
  label: string
  /** How many of the template's documents this reading covers. */
  documents: number
  /** True on the reading the documents currently carry. */
  delivered: boolean
}

export const DELIVERED_OPTION: ArmOption = {
  value: DELIVERED,
  arm: DELIVERED,
  label: 'Gelieferte Werte',
  documents: 0,
  delivered: true,
}

function armLabel(arm: string): string {
  return STAGE_TITLES[arm as RecognitionArm] ?? arm
}

/**
 * A reading's key.
 *
 * A variant is keyed `(document, arm, model)`, so two models on one arm are
 * two readings and have to be two options — a picker keyed by the arm alone
 * would leave the choice between them to the endpoint (newest per document
 * wins) and could show a reading nobody asked for. `|` is safe as the
 * separator: arm ids come from a fixed set and carry none.
 */
export function armValue(request: ArmRequest): string {
  if (request.arm === DELIVERED) return DELIVERED
  return `${request.arm}|${request.model ?? ''}`
}

/** The inverse, for turning a `<select>` value back into a request. Null for
 *  the delivered values, which are no arm. */
export function parseArmValue(value: string): ArmRequest | null {
  if (value === DELIVERED) return null
  const separator = value.indexOf('|')
  if (separator < 0) return { arm: value }
  const model = value.slice(separator + 1)
  return { arm: value.slice(0, separator), ...(model ? { model } : {}) }
}

/**
 * One option per reading actually stored on this template — that is, per arm
 * **and model**, with the delivered reading at the top.
 *
 * `GET /v1/templates/:id/arms` reports one row per arm and model, and
 * `GET …/variant-records` now takes `&model=` beside `&arm=`, so the two
 * finally agree and a template read by two models offers both. Leaving the
 * model off falls back to the newest reading per document, which is a
 * defensible default for an export and a confusing one for a picker.
 */
export function armOptions(summaries: ArmSummary[] | undefined): ArmOption[] {
  const byReading = new Map<string, ArmOption>()
  for (const summary of summaries ?? []) {
    if (summary.documents <= 0) continue
    const request: ArmRequest = {
      arm: summary.arm,
      ...(summary.model ? { model: summary.model } : {}),
    }
    const value = armValue(request)
    const existing = byReading.get(value)
    if (existing) {
      existing.documents = Math.max(existing.documents, summary.documents)
      existing.delivered = existing.delivered || summary.delivered
      continue
    }
    byReading.set(value, {
      ...request,
      value,
      label: armLabel(summary.arm),
      documents: summary.documents,
      delivered: summary.delivered,
    })
  }
  return [DELIVERED_OPTION, ...byReading.values()]
}

/** The reading's name without its coverage — for a running text, where "31
 *  Dokumente" is a number the sentence already carries. */
export function armOptionName(option: ArmOption): string {
  return option.model ? `${option.label} (${option.model})` : option.label
}

/** What the picker shows for one option: "KI-Bild · gemini-3.8-flash · 31
 *  Dokumente". The model belongs in the name because it is what tells two
 *  readings of one arm apart; the delivered marker stays a separate element,
 *  so a screen reader does not read a badge as part of the name. */
export function armOptionLabel(option: ArmOption): string {
  if (option.value === DELIVERED) return option.label
  const documents = `${option.documents} ${option.documents === 1 ? 'Dokument' : 'Dokumente'}`
  return [option.label, option.model, documents].filter(Boolean).join(' · ')
}
